"use client";
import { useEffect, useState } from "react";

const OAUTH_ERRORS: Record<string, string> = {
  missing_code: "Google didn't return an authorization code — please try again.",
  bad_state:    "Security check failed. Try signing in again.",
  no_email:     "Your Google account didn't share an email — please choose another.",
  oauth_failed: "Google sign-in failed. Please try again.",
};

export default function LoginPage() {
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Surface OAuth callback errors passed as ?error=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err) setError(OAUTH_ERRORS[err] ?? err);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await fetch("/api/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (res.ok) {
      window.location.href = "/";
    } else {
      const d = await res.json();
      setError(d.error ?? "Login failed");
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F6F6F6] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#101828]">ReFlow</h1>
          <p className="text-sm text-[#667085] mt-1">Turn conversations into action · Powered by Recykal</p>
        </div>
        <div className="bg-white rounded-xl shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] border border-[#EAECF0] p-8">
          <h2 className="text-base font-semibold text-[#344054] mb-6">Sign in</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#344054] mb-1.5">User Name</label>
              <input
                autoFocus
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter Username"
                className="w-full border border-[#D0D5DD] rounded-lg px-4 py-2.5 text-sm text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[#004EEB]/30 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#344054] mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full border border-[#D0D5DD] rounded-lg px-4 py-2.5 text-sm text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[#004EEB]/30 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
                required
              />
              {error && <p className="text-xs text-[#DC2626] mt-2">{error}</p>}
            </div>
            <button
              type="submit"
              disabled={loading || !password || !username}
              className="w-full bg-[#004EEB] hover:bg-[#155EEF] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#EAECF0]" />
            <span className="text-xs text-[#98A2B3]">or</span>
            <div className="flex-1 h-px bg-[#EAECF0]" />
          </div>

          <a
            href="/api/auth/google"
            className="w-full flex items-center justify-center gap-2.5 border border-[#D0D5DD] hover:bg-[#F9FAFB] text-[#344054] font-semibold py-2.5 rounded-lg text-sm transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </a>
        </div>
      </div>
    </div>
  );
}
